import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  useConnection,
  useNodesInitialized,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { layout as dagreLayout } from 'dagre-d3-es/src/dagre/index.js'
import { Graph as DagreGraph } from 'dagre-d3-es/src/graphlib/index.js'
import { AlertTriangle, ChevronDown, ShieldCheck } from 'lucide-react'
import type { OrgChartDTO, OrgChartGapReason, OrgChartNodeDTO } from '@deepguide-ai/types'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { trpc } from '@/lib/trpc'
import { Link, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { EditEmployeeDialog } from '@/components/compliance/people/EditEmployeeDialog'

const NODE_WIDTH = 220
const NODE_HEIGHT = 72

const GAP_REASON: Record<OrgChartGapReason, string> = {
  NO_MANAGER: 'no manager recorded',
  MANAGER_INACTIVE: 'their manager has left or is excluded',
  MANAGER_UNLINKED: 'their manager is not a person in the roster',
}

type OrgNode = Node<{ person: OrgChartNodeDTO; flagged: boolean; editable: boolean }, 'person'>

function PersonCard({ id, data }: NodeProps<OrgNode>) {
  const { person, flagged, editable } = data
  // While a manager line is being dragged, every other card becomes a drop zone.
  const drag = useConnection((c) =>
    c.inProgress
      ? { fromId: c.fromNode.id, fromType: c.fromHandle.type, over: c.toNode?.id === id, valid: c.isValid }
      : null
  )
  const dropZone = editable && drag && drag.fromId !== id
  return (
    <div
      className={cn(
        'relative rounded-sm border bg-card px-3 py-2 shadow-sm',
        flagged ? 'border-status-amber-border' : 'border-border',
        editable && !drag && 'cursor-pointer hover:border-ring',
        dropZone && 'border-dashed border-ring',
        dropZone && drag.over && (drag.valid === false ? 'border-solid border-destructive' : 'border-solid border-ring ring-2 ring-ring/40')
      )}
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
    >
      {/* Drag from this dot onto the manager's card to set who they report to. */}
      <Handle
        type='target'
        position={Position.Top}
        isConnectable={editable}
        title={editable ? 'Drag onto their manager' : undefined}
        className={cn('!border-0 !bg-border', editable ? '!h-2.5 !w-2.5 hover:!bg-ring' : '!h-1.5 !w-1.5')}
      />
      <div className='flex items-center gap-1.5'>
        <span className='truncate text-[13px] font-semibold text-foreground'>{person.name}</span>
        {person.holdsSecurityRole && (
          <ShieldCheck className='h-3.5 w-3.5 shrink-0 text-cobalt-strong' aria-label='Security Lead' />
        )}
      </div>
      <div className='truncate text-[12px] text-text-body'>{person.title ?? 'No title'}</div>
      <div className='truncate text-[11px] text-text-subtle'>{person.department ?? 'No department'}</div>
      <Handle
        type='source'
        position={Position.Bottom}
        isConnectable={editable}
        className={cn('!border-0 !bg-border', editable ? '!h-2.5 !w-2.5 hover:!bg-ring' : '!h-1.5 !w-1.5')}
      />
      {/* Rendered last: edges without a handle id attach to the first handle, which must stay the dot. */}
      {dropZone && (
        <Handle
          id='drop'
          type={drag.fromType === 'target' ? 'source' : 'target'}
          position={drag.fromType === 'target' ? Position.Bottom : Position.Top}
          className='!absolute !inset-0 !h-full !w-full !translate-x-0 !translate-y-0 !transform-none !rounded-sm !border-0 !bg-transparent'
        />
      )}
    </div>
  )
}

const nodeTypes = { person: PersonCard }

function toFlow(chart: OrgChartDTO, editable: boolean): { nodes: OrgNode[]; edges: Edge[] } {
  const flagged = new Set([
    ...chart.gaps.unplaced.map((u) => u.employeeId),
    ...chart.gaps.cycles.flat(),
  ])
  const graph = new DagreGraph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({ rankdir: 'TB', nodesep: 24, ranksep: 64, marginx: 20, marginy: 20 })
  for (const person of chart.nodes) graph.setNode(person.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  const edges: Edge[] = []
  for (const person of chart.nodes) {
    if (!person.managerEmployeeId) continue
    graph.setEdge(person.managerEmployeeId, person.id)
    edges.push({
      id: `${person.managerEmployeeId}-${person.id}`,
      source: person.managerEmployeeId,
      target: person.id,
      type: 'smoothstep',
      // Arrow points from the manager to the person who reports to them.
      markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--text-subtle)', width: 16, height: 16 },
      style: { stroke: 'var(--text-subtle)', strokeWidth: 1.5 },
    })
  }
  dagreLayout(graph)
  const nodes: OrgNode[] = chart.nodes.map((person) => {
    const { x, y } = graph.node(person.id)
    return {
      id: person.id,
      type: 'person',
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      data: { person, flagged: flagged.has(person.id), editable },
      draggable: false,
    }
  })
  return { nodes, edges }
}

/** One sentence per reporting-line gap. */
function orgChartGapLines(chart: OrgChartDTO): string[] {
  const { unplaced, cycles, noSecurityRoleHolder } = chart.gaps
  const name = (id: string) => chart.nodes.find((n) => n.id === id)?.name ?? 'Unknown'
  return [
    ...(noSecurityRoleHolder
      ? ["No one holds the Security Lead role, so the security function's reporting line is not shown."]
      : []),
    ...unplaced.map(
      (u) =>
        `${name(u.employeeId)} is not placed under anyone: ${GAP_REASON[u.reason]}${u.managerName ? ` (${u.managerName})` : ''}.`
    ),
    ...cycles.map((cycle) => `Reporting cycle: ${[...cycle, cycle[0]].map(name).join(' → ')}.`),
  ]
}

function OrgChartGaps({ chart }: { chart: OrgChartDTO }) {
  const lines = orgChartGapLines(chart)
  if (lines.length === 0) return null
  return (
    <details className='group rounded-md border border-status-amber-border bg-status-amber-subtle px-3 py-2 text-[12px] text-status-amber-strong'>
      <summary className='flex cursor-pointer list-none items-center gap-1.5 font-semibold [&::-webkit-details-marker]:hidden'>
        <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
        An auditor would question {lines.length} reporting {lines.length === 1 ? 'line' : 'lines'}
        <span className='ml-auto flex items-center gap-1 font-normal'>
          <span className='group-open:hidden'>View details</span>
          <span className='hidden group-open:inline'>Hide details</span>
          <ChevronDown className='h-3.5 w-3.5 transition-transform group-open:rotate-180' />
        </span>
      </summary>
      <OrgChartGapList chart={chart} className='mt-2 max-h-60 overflow-y-auto' />
    </details>
  )
}

/** The gap sentences as a list; shared by the chart's warning and the PDF export. */
export function OrgChartGapList({ chart, className }: { chart: OrgChartDTO; className?: string }) {
  return (
    <ul className={cn('list-disc space-y-0.5 pl-5', className)}>
      {orgChartGapLines(chart).map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

const CANVAS_MARGIN = 20

/**
 * #1995 — the whole tree at 1:1 for the PDF export: no pan, zoom or controls,
 * and sized to the layout's bounds so no node is clipped. `onReady` fires once
 * React Flow has measured the nodes, which is when the edges are drawn.
 */
export function OrgChartCanvas({ chart, onReady }: { chart: OrgChartDTO; onReady: () => void }) {
  const flow = useMemo(() => toFlow(chart, false), [chart])
  const width = Math.max(...flow.nodes.map((n) => n.position.x + NODE_WIDTH)) + CANVAS_MARGIN
  const height = Math.max(...flow.nodes.map((n) => n.position.y + NODE_HEIGHT)) + CANVAS_MARGIN
  return (
    <div style={{ width, height }}>
      {/* Uncontrolled: with no onNodesChange, controlled nodes never report as measured. */}
      <ReactFlow
        defaultNodes={flow.nodes}
        defaultEdges={flow.edges}
        nodeTypes={nodeTypes}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <NotifyWhenMeasured onReady={onReady} />
      </ReactFlow>
    </div>
  )
}

function NotifyWhenMeasured({ onReady }: { onReady: () => void }) {
  const measured = useNodesInitialized()
  useEffect(() => {
    if (measured) requestAnimationFrame(onReady)
  }, [measured, onReady])
  return null
}

/**
 * #1995 — the org chart is a view of the People roster. `editable` (People
 * page) opens the employee's edit dialog on click, so changing a manager or
 * title writes the Employee row and the chart can't drift from the roster.
 * The Employee Center renders it read-only.
 */
export function OrgChart({
  editable = false,
  inKeyDocument = false,
}: {
  editable?: boolean
  /** Rendered on the Organization chart key document: link to People, not back to itself. */
  inKeyDocument?: boolean
}) {
  const { workspaceId } = useParams({ strict: false })
  const chartQuery = useQuery(trpc.employee.orgChart.queryOptions())
  const [editingId, setEditingId] = useState<string | null>(null)
  const employeeQuery = useQuery({
    ...trpc.employee.get.queryOptions({ id: editingId ?? '' }),
    enabled: editable && Boolean(editingId),
  })
  // The person was deleted since the chart loaded: let the node be clicked again.
  useEffect(() => {
    if (employeeQuery.isFetched && !employeeQuery.data) setEditingId(null)
  }, [employeeQuery.isFetched, employeeQuery.data])
  const queryClient = useQueryClient()
  const reassign = useMutation({
    ...trpc.employee.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.employee.orgChart.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.employee.list.queryKey() })
    },
    onError: (err: Error) => toast.error(err.message || 'Could not change the manager'),
  })
  // React Flow normalizes either drag direction to source = manager, target = report.
  // Refuses self-links and placing someone under their own report (a reporting cycle).
  const canReport = ({ source: managerId, target: reportId }: Pick<Connection, 'source' | 'target'>) => {
    const chart = chartQuery.data
    if (!chart || !managerId || !reportId || managerId === reportId) return false
    const managerOf = new Map(chart.nodes.map((n) => [n.id, n.managerEmployeeId]))
    for (let cur: string | null | undefined = managerId, i = 0; cur && i < chart.nodes.length; cur = managerOf.get(cur), i++) {
      if (cur === reportId) return false
    }
    return true
  }
  const onConnect = ({ source: managerId, target: reportId }: Connection) => {
    const chart = chartQuery.data
    if (!chart || !canReport({ source: managerId, target: reportId })) return
    const manager = chart.nodes.find((n) => n.id === managerId)
    reassign.mutate({ id: reportId, managerEmployeeId: managerId, manager: manager?.name ?? null })
  }
  const flow = useMemo(
    () => (chartQuery.data ? toFlow(chartQuery.data, editable) : null),
    [chartQuery.data, editable]
  )

  if (chartQuery.isPending) return <TableSkeleton />
  if (!chartQuery.data || !flow) {
    return <p className='text-[13px] text-text-subtle'>The org chart could not be loaded.</p>
  }
  if (chartQuery.data.nodes.length === 0) {
    return <p className='text-[13px] text-text-subtle'>No one is in the People roster yet.</p>
  }

  return (
    <div className='space-y-3'>
      <OrgChartGaps chart={chartQuery.data} />
      {editable && (
        <p className='text-[12px] text-text-subtle'>
          Click a person to edit them. To change who someone reports to, drag the dot above their card onto their
          manager's card.
          {inKeyDocument ? (
            <>
              {' '}To edit someone's details or add people, go to{' '}
              <Link
                to='/workspaces/$workspaceId/people'
                params={{ workspaceId: workspaceId! }}
                search={{ tab: 'directory' }}
                className='text-cobalt-strong hover:underline'
              >
                People
              </Link>
              .
            </>
          ) : (
            <>
              {' '}The chart is your audit evidence: generate its dated report from the{' '}
              <Link
                to='/workspaces/$workspaceId/documents'
                params={{ workspaceId: workspaceId! }}
                search={{ tab: 'key', keyDocument: 'org-chart' }}
                className='text-cobalt-strong hover:underline'
              >
                Organization chart key document
              </Link>
              .
            </>
          )}
        </p>
      )}
      <div className='h-[640px] rounded-md border bg-card'>
        <ReactFlow
          nodes={flow.nodes}
          edges={flow.edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={editable}
          onConnect={editable ? onConnect : undefined}
          isValidConnection={canReport}
          onNodeClick={editable ? (_, node) => setEditingId(node.id) : undefined}
        >
          <Background gap={18} size={1} color='var(--border)' />
          <Controls
            showInteractive={false}
            className="shadow-xs border rounded-md overflow-hidden [&>button]:!bg-background [&>button]:dark:!bg-input/30 [&>button]:!text-foreground [&>button]:!border-border"
          />
        </ReactFlow>
      </div>
      {editable && (
        <EditEmployeeDialog
          employee={employeeQuery.data ?? null}
          open={Boolean(editingId && employeeQuery.data)}
          onOpenChange={(open) => !open && setEditingId(null)}
        />
      )}
    </div>
  )
}
